import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Numeros from './components/Numeros';
import CardLink from './components/CardLink';
import ChipsFiltro from './components/ChipsFiltro';
import CartaoCrianca from './components/CartaoCrianca';
import FamiliasSemDados from './components/FamiliasSemDados';
import Impressao from './components/Impressao';
import { useRelatorioCriancas } from './useRelatorio';
import {
  aplicarFiltro,
  contar,
  familiasSemDados,
  temCrianca,
  textoParaWhatsapp,
} from './utils';
import type { Filtro } from './types';

export default function CriancasDoEventoPage() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const numero = Number(eventoId);

  const { estado, linhas, eventoNome, erro, recarregar } = useRelatorioCriancas(numero);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 3000);
    return () => clearTimeout(t);
  }, [copiado]);

  const contagens = useMemo(() => contar(linhas), [linhas]);
  const criancas = useMemo(() => linhas.filter(temCrianca), [linhas]);
  const filtradas = useMemo(() => aplicarFiltro(criancas, filtro), [criancas, filtro]);
  const semDados = useMemo(() => familiasSemDados(linhas), [linhas]);

  async function copiarParaWhatsapp() {
    try {
      await navigator.clipboard.writeText(textoParaWhatsapp(linhas));
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#f6f7fb]">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="app-entra mx-auto w-full min-w-0 max-w-[1080px] px-4 py-5 pb-16">
        <div className="print:hidden">
          {/* Voltar + título + ações */}
          <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => navigate(`/gerenciar/${numero}`)}
                className="flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-200 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-left-line" aria-hidden />
                </span>
                Voltar
              </button>
              <h1 className="mt-2 text-[22px] font-extrabold leading-tight text-foreground-950">
                Crianças no acampamento
              </h1>
              {eventoNome && (
                <p className="mt-0.5 text-[13px] text-foreground-600">{eventoNome}</p>
              )}
            </div>

            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => window.print()}
                disabled={estado !== 'pronto'}
                className="flex h-11 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-2xl border border-background-300 bg-white px-3.5 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-printer-line" aria-hidden />
                </span>
                Imprimir
              </button>
              <button
                type="button"
                onClick={copiarParaWhatsapp}
                disabled={estado !== 'pronto' || criancas.length === 0}
                className="flex h-11 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-2xl bg-primary-500 px-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-500"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
                </span>
                {copiado ? 'Texto copiado' : 'Copiar para o WhatsApp'}
              </button>
            </div>
          </div>

          {estado === 'carregando' && (
            <div className="mt-4 space-y-2.5">
              <div className="esqueleto h-[92px] rounded-2xl bg-white" />
              <div className="esqueleto h-[120px] rounded-2xl bg-white" />
              <div className="esqueleto h-[260px] rounded-2xl bg-white" />
            </div>
          )}

          {estado === 'erro' && (
            <div className="mt-4 rounded-2xl border border-background-200 bg-white px-4 py-8 text-center">
              <h2 className="text-[16px] font-bold text-foreground-950">
                Não foi possível abrir a lista
              </h2>
              <p className="mt-1 text-[14px] text-foreground-600">
                {erro ?? 'Tente de novo em instantes.'}
              </p>
              <div className="mt-4 flex flex-col justify-center gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={recarregar}
                  className="h-12 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-4 text-[14px] font-semibold text-white transition-colors hover:bg-primary-600 sm:w-auto"
                >
                  Tentar de novo
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/gerenciar/${numero}`)}
                  className="h-12 w-full cursor-pointer whitespace-nowrap rounded-2xl border border-background-300 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-100 sm:w-auto"
                >
                  Voltar ao painel
                </button>
              </div>
            </div>
          )}

          {estado === 'pronto' && (
            <>
              <div className="mt-4">
                <Numeros contagens={contagens} />
              </div>

              <div className="mt-2.5">
                <CardLink eventoId={numero} />
              </div>

              {criancas.length === 0 && semDados.length === 0 ? (
                <div className="mt-3 rounded-2xl border border-background-200 bg-white px-4 py-10 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                    <i className="ri-user-heart-line text-2xl" aria-hidden />
                  </span>
                  <p className="mt-3 text-[15px] font-semibold text-foreground-950">
                    Ninguém da equipe marcou que vai levar filhos.
                  </p>
                  <p className="mt-1 text-[13px] text-foreground-600">
                    Mande o link acima no grupo da equipe para os pais preencherem.
                  </p>
                </div>
              ) : (
                <>
                  {criancas.length > 0 && (
                    <div className="mt-4 min-w-0 max-w-full">
                      <ChipsFiltro valor={filtro} aoMudar={setFiltro} />
                    </div>
                  )}

                  {filtradas.length === 0 && criancas.length > 0 && (
                    <div className="mt-3 rounded-2xl border border-background-200 bg-white px-4 py-8 text-center">
                      <p className="text-[14px] text-foreground-700">
                        Nenhuma criança neste filtro.
                      </p>
                      <button
                        type="button"
                        onClick={() => setFiltro('todas')}
                        className="mt-3 h-11 cursor-pointer whitespace-nowrap rounded-2xl border border-background-300 bg-white px-4 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                      >
                        Ver todas
                      </button>
                    </div>
                  )}

                  {filtradas.length > 0 && (
                    <div className="mt-3 grid w-full min-w-0 grid-cols-1 gap-2.5 lg:grid-cols-2">
                      {filtradas.map((linha, indice) => (
                        <div key={`${linha.crianca}-${indice}`} className="min-w-0">
                          <CartaoCrianca linha={linha} />
                        </div>
                      ))}
                    </div>
                  )}

                  <FamiliasSemDados eventoId={numero} familias={semDados} />
                </>
              )}
            </>
          )}
        </div>

        {estado === 'pronto' && <Impressao linhas={linhas} eventoNome={eventoNome} />}
      </div>
    </div>
  );
}